import type { LeadFormEditorData } from '@plura/shared/types';
import { applyLayouts } from './applyLayouts';
import { assignVisuals } from './assignVisuals';
import type { DesignVariant, VariantMediaPool, VariantTiers } from './types';

export type AppliedVariantPatch = Partial<
  Pick<
    LeadFormEditorData,
    | 'backgroundColorSetting'
    | 'textColorSetting'
    | 'buttonColorSetting'
    | 'buttonTextColorSetting'
    | 'fontFamilySetting'
    | 'elementType'
    | 'journeyItems'
  >
>;

/**
 * Build the patch of changes to apply to the current form for a given variant
 * + tier selection. The order is `theme -> visuals -> layouts` so that layouts
 * sanitize on top of any media we just assigned.
 *
 * Returns only the keys that should change. Callers can spread or
 * `form.setValue` field-by-field.
 */
export function applyVariant(
  current: LeadFormEditorData,
  variant: DesignVariant,
  tiers: VariantTiers,
  pool: VariantMediaPool,
): AppliedVariantPatch {
  const patch: AppliedVariantPatch = {};

  if (tiers.theme) {
    patch.backgroundColorSetting = variant.theme.backgroundColorSetting;
    patch.textColorSetting = variant.theme.textColorSetting;
    patch.buttonColorSetting = variant.theme.buttonColorSetting;
    patch.buttonTextColorSetting = variant.theme.buttonTextColorSetting;
    patch.fontFamilySetting = variant.theme.fontFamilySetting;
    patch.elementType = variant.theme.elementType;
  }

  let nextItems = current.journeyItems;
  let touchedItems = false;

  if (tiers.visuals) {
    nextItems = assignVisuals(nextItems, pool, variant);
    touchedItems = true;
  }

  if (tiers.layouts) {
    nextItems = applyLayouts(nextItems, variant.layoutStyle);
    touchedItems = true;
  }

  if (touchedItems) {
    patch.journeyItems = nextItems;
  }

  return patch;
}

/**
 * Convenience: produce a full LeadFormEditorData with the variant patch applied.
 * Useful for previews where we need a complete form to render.
 *
 * The result is deep-cloned so that previews can be rendered (and mutated by
 * downstream form/render machinery) without ever bleeding back into the caller's
 * live editor state. Without this, e.g. theme-only previews share the journey
 * items array with `current` and any in-place edit propagates back.
 */
export function previewWithVariant(
  current: LeadFormEditorData,
  variant: DesignVariant,
  tiers: VariantTiers,
  pool: VariantMediaPool,
): LeadFormEditorData {
  const patch = applyVariant(current, variant, tiers, pool);
  const merged = { ...current, ...patch };
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(merged);
    } catch {
      // fall through to JSON fallback
    }
  }
  return JSON.parse(JSON.stringify(merged)) as LeadFormEditorData;
}
